import * as React from 'react';

import HistoryTabs from 'Components/HistoryTabs';
import { useAppSelector } from 'Store/index';
import { ninmtFileTypes } from 'Routes/PatientFiles/utils';
import { Container, Item } from 'Components/Grid';
import FilePreviewBox from 'Components/_NewElements/FilePreviewBox';
import FileCompareDialog from 'Components/FileCompareDialog';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import AddNewDialog from '../Form/AddNewDialog';
import { useIntl } from 'react-intl';
import EditFileDialog from '../Form/EditFileDialog';
import { dateFromPartial, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import colors from '../../../../config/theme/colors';
import DeleteDialog from 'Components/HistoryRowControls/DeleteDialog';

const NINMTPainAndSymptomsHistory = ({ files, fm }: ININMTHistory) => {
  const [compareDialogFile, setCompareDialogFile] = React.useState<(TFILE_METADATA & IControlProps) | undefined>(
    undefined,
  );

  const docs = useAppSelector(s => s.documents.sortedAndMergedDocuments) || [];
  const treatmentDocs = (docs.filter(d => d._type === 'ninmtTreatmentPeriod') as Array<ININMTTreatmentPeriod>).toSorted(
    (a, b) => sortPartialDate(b.date, a.date),
  );

  const periodDates = treatmentDocs.map(d => d.date).filter(d => d) as PartialDate[];

  const periodDatesWithFiles = periodDates.map((date, i) => ({
    date,
    dateFiles: files.filter(f => {
      if (i === 0) return f.date ? new Date(f.date) >= dateFromPartial(date) : [];
      else {
        const prevDate = periodDates[i - 1];
        return f.date ? new Date(f.date) >= dateFromPartial(date) && new Date(f.date) < dateFromPartial(prevDate) : [];
      }
    }),
  }));

  // Add one more item to catch files that come before the first treatment doc
  periodDatesWithFiles.push({
    date: [0, 0, 0],
    dateFiles: files.filter(f => {
      const prevDate = periodDatesWithFiles.at(-1)?.date;
      return f.date && prevDate ? new Date(f.date) < dateFromPartial(prevDate) : [];
    }),
  });

  const [deletingFile, setDeletingFile] = React.useState<TFile | null>(null);
  const [editID, setEditID] = React.useState<string | null>(null);

  return (
    <div>
      {periodDatesWithFiles.map(dd => {
        return (
          <div key={dd.date.toString()} style={{ width: '100%' }}>
            <Container style={{ display: 'flex' }}>
              {dd.dateFiles.map(file => (
                <Item xs={10} md={7} key={file._id} style={{ margin: '0 2rem 1rem 0' }}>
                  <FilePreviewBox
                    name={file.name}
                    date={file.date ?? undefined}
                    showArrow
                    arrow={'maximize'}
                    file={file}
                    controls={{
                      edit: { onClick: () => setEditID(file._id) },
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      setCompareDialogFile(file);
                    }}
                  />
                </Item>
              ))}
            </Container>

            {dd.date && dd.date[0] !== 0 ? (
              <div
                style={{
                  display: 'flex',
                  margin: '2rem 0',
                  color: colors.appBlue.default,
                  fontWeight: 600,
                  marginTop: '1rem',
                  marginBottom: '4rem',
                }}
              >
                {/* <Icon icon="upArrow" /> */}
                {fm('diagnosis.ninmt.treatmentDecisionDate')}
                {': '}
                {formatPartialDate(dd.date)}
              </div>
            ) : (
              <></>
            )}
          </div>
        );
      })}
      <FileCompareDialog
        files={files}
        firstCompare={compareDialogFile}
        closeDialog={() => setCompareDialogFile(undefined)}
      />
      {deletingFile && (
        <DeleteDialog document={deletingFile} open={!!deletingFile} setOpen={() => setDeletingFile(null)} />
      )}
      <EditFileDialog
        editingFileData={files.find(f => f._id === editID) || null}
        onCloseEditing={() => setEditID(null)}
      />
    </div>
  );
};

interface ININMTHistory {
  files: IOwnProps['files'];
  fm: (id: string) => string;
}

const filesHistory = (
  platform: string,
  files: IOwnProps['files'],
  addNewHandler: (fileType: string) => void,
  fm: (id: string) => string,
) =>
  ({
    ninmt: [
      {
        title: 'patientFiles.painAndSymptomMaps',
        content: (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ActionButtonRounded
                text="general.new"
                height={3.5}
                width={'auto'}
                fontSize={14}
                onClick={() => addNewHandler('painAndSymptomMap')}
              />
            </div>
            <NINMTPainAndSymptomsHistory files={files.filter(f => ninmtFileTypes.includes(f.subType ?? ''))} fm={fm} />
          </>
        ),
      },
    ],
  })?.[platform || ''];

const FilesList = ({ files }: IOwnProps): React.JSX.Element => {
  const platform = useAppSelector(s => s.session.platforms?.selected);

  const sortedFiles = files.toSorted((a, b) => {
    const aDate = a.date ? new Date(a.date) : new Date(a._docCreateDate);
    const bDate = b.date ? new Date(b.date) : new Date(b._docCreateDate);
    return bDate.valueOf() - aDate.valueOf();
  });

  const [newFileType, setNewFileType] = React.useState<string>('');
  // Temporary attachments used for handling the attachment form
  const [tempAttachments, setTempAttachments] = React.useState<File[] | null>(null);
  const handleTempFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAttachments([...(tempAttachments || []), e.target.files[0]]);
    }
  };

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <>
      <HistoryTabs>
        {[
          ...(filesHistory(
            platform ?? '',
            sortedFiles,
            (fileType: string) => {
              setNewFileType(fileType);
              setTempAttachments([]);
            },
            fm,
          ) ?? []),
        ]}
      </HistoryTabs>
      <AddNewDialog
        newAttachments={tempAttachments}
        setNewAttachments={setTempAttachments}
        handleFileChange={handleTempFileChange}
        fileType={newFileType}
      />
    </>
  );
};

interface IOwnProps {
  files: Array<TFILE_METADATA & IControlProps>;
}

export default FilesList;
